/**
 * خدمة تتبع الزوار الذين دخلوا للمنصة
 */

import { collection, addDoc, query, orderBy, limit, getDocs, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const VISITORS_COLLECTION = 'platform_visitors';
const MAX_VISITORS = 1000; // الحد الأقصى لعدد الزوار المحفوظين

/**
 * تسجيل زائر جديد في Firebase
 */
export async function trackVisitor() {
  try {
    const visitor = {
      timestamp: Timestamp.now(),
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      referrer: document.referrer || 'direct',
      url: window.location.href,
    };

    await addDoc(collection(db, VISITORS_COLLECTION), visitor);
    
    // أيضاً احفظ محلياً كنسخة احتياطية
    trackVisitorLocal({
      ...visitor,
      timestamp: new Date().toISOString(),
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[VisitorTracking] تم تسجيل زائر جديد في Firebase:', visitor);
    }
  } catch (error) {
    console.error('[VisitorTracking] خطأ في تسجيل الزائر:', error);
    // Fallback إلى localStorage في حالة فشل Firebase
    trackVisitorLocal({
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      referrer: document.referrer || 'direct',
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * تسجيل زائر محلي (fallback)
 */
function trackVisitorLocal(visitor) {
  try {
    const VISITORS_KEY = 'platform_visitors';
    const visitors = getVisitorsLocal();
    visitors.unshift({
      ...visitor,
      id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9),
      timestamp: visitor.timestamp || new Date().toISOString(),
    });

    if (visitors.length > MAX_VISITORS) {
      visitors.splice(MAX_VISITORS);
    }

    localStorage.setItem(VISITORS_KEY, JSON.stringify(visitors));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[VisitorTracking] تم تسجيل زائر محلياً:', visitor);
    }
  } catch (error) {
    console.error('[VisitorTracking] خطأ في تسجيل الزائر محلياً:', error);
  }
}

/**
 * جلب جميع الزوار من Firebase
 */
export async function getVisitors() {
  try {
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const q = query(visitorsRef, orderBy('timestamp', 'desc'), limit(MAX_VISITORS));
    const snapshot = await getDocs(q);
    
    const visitors = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      visitors.push({
        id: doc.id,
        timestamp: data.timestamp?.toDate?.()?.toISOString() || data.timestamp || new Date().toISOString(),
        userAgent: data.userAgent || 'Unknown',
        language: data.language || 'Unknown',
        platform: data.platform || 'Unknown',
        screenWidth: data.screenWidth || 0,
        screenHeight: data.screenHeight || 0,
        referrer: data.referrer || 'direct',
        url: data.url || (typeof window !== 'undefined' ? window.location.href : 'Unknown'),
      });
    });
    
    // دمج مع البيانات المحلية
    const localVisitors = getVisitorsLocal();
    const allVisitors = [...visitors, ...localVisitors];
    
    // إزالة التكرارات بناءً على timestamp و userAgent
    const uniqueVisitors = allVisitors.filter((v, index, self) =>
      index === self.findIndex((t) => 
        t.timestamp === v.timestamp && 
        t.userAgent === v.userAgent &&
        Math.abs(new Date(t.timestamp) - new Date(v.timestamp)) < 1000
      )
    );
    
    // ترتيب حسب التاريخ (الأحدث أولاً)
    uniqueVisitors.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB - dateA;
    });
    
    return uniqueVisitors.slice(0, MAX_VISITORS);
  } catch (error) {
    console.error('[VisitorTracking] خطأ في جلب الزوار من Firebase:', error);
    // Fallback إلى localStorage
    const localVisitors = getVisitorsLocal();
    // ترتيب حسب التاريخ (الأحدث أولاً)
    localVisitors.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB - dateA;
    });
    return localVisitors.slice(0, MAX_VISITORS);
  }
}

/**
 * جلب الزوار محلياً (fallback)
 */
function getVisitorsLocal() {
  try {
    const VISITORS_KEY = 'platform_visitors';
    const data = localStorage.getItem(VISITORS_KEY);
    if (!data) {
      return [];
    }
    const visitors = JSON.parse(data);
    // التأكد من أن كل زائر له id
    return visitors.map((v, index) => ({
      ...v,
      id: v.id || `local_${index}_${Date.now()}`,
    }));
  } catch (error) {
    console.error('[VisitorTracking] خطأ في جلب الزوار محلياً:', error);
    return [];
  }
}

/**
 * الاشتراك في تحديثات الزوار في الوقت الفعلي
 */
export function subscribeToVisitors(callback) {
  try {
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const q = query(visitorsRef, orderBy('timestamp', 'desc'), limit(MAX_VISITORS));
    
    return onSnapshot(q, (snapshot) => {
      const visitors = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        visitors.push({
          id: doc.id,
          timestamp: data.timestamp?.toDate?.()?.toISOString() || data.timestamp,
          userAgent: data.userAgent,
          language: data.language,
          platform: data.platform,
          screenWidth: data.screenWidth,
          screenHeight: data.screenHeight,
          referrer: data.referrer,
          url: data.url,
        });
      });
      callback(visitors);
    }, (error) => {
      console.error('[VisitorTracking] خطأ في الاشتراك في الزوار:', error);
      // Fallback إلى localStorage
      callback(getVisitorsLocal());
    });
  } catch (error) {
    console.error('[VisitorTracking] خطأ في الاشتراك في الزوار:', error);
    // Fallback إلى localStorage
    callback(getVisitorsLocal());
  }
}

/**
 * حذف جميع الزوار من Firebase
 */
export async function clearVisitors() {
  try {
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const snapshot = await getDocs(visitorsRef);
    
    const deletePromises = [];
    snapshot.forEach((doc) => {
      deletePromises.push(doc.ref.delete());
    });
    
    await Promise.all(deletePromises);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[VisitorTracking] تم حذف جميع الزوار من Firebase');
    }
  } catch (error) {
    console.error('[VisitorTracking] خطأ في حذف الزوار من Firebase:', error);
    // Fallback إلى localStorage
    try {
      localStorage.removeItem('platform_visitors');
    } catch (localError) {
      console.error('[VisitorTracking] خطأ في حذف الزوار محلياً:', localError);
    }
  }
}

/**
 * تنسيق التاريخ والوقت
 */
export function formatDateTime(isoString) {
  try {
    const date = new Date(isoString);
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('ar-SA', options);
  } catch (error) {
    return isoString;
  }
}

/**
 * استخراج معلومات المتصفح من User Agent
 */
export function parseUserAgent(userAgent) {
  const ua = userAgent.toLowerCase();
  
  let browser = 'غير معروف';
  let device = 'غير معروف';
  
  // تحديد المتصفح
  if (ua.includes('chrome') && !ua.includes('edg')) {
    browser = 'Chrome';
  } else if (ua.includes('firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('safari') && !ua.includes('chrome')) {
    browser = 'Safari';
  } else if (ua.includes('edg')) {
    browser = 'Edge';
  } else if (ua.includes('opera') || ua.includes('opr')) {
    browser = 'Opera';
  }
  
  // تحديد الجهاز
  if (ua.includes('mobile') || ua.includes('android')) {
    device = 'هاتف';
  } else if (ua.includes('tablet') || ua.includes('ipad')) {
    device = 'تابلت';
  } else {
    device = 'كمبيوتر';
  }
  
  return { browser, device };
}

